import React, { useMemo } from "react";
import ReactPaginate from "react-paginate";
import { classNames } from "Utils/utils";
import SelectOption from "../Select/Select";

const COMPACT_PAGE_SIZES = [10, 25, 50];

const CompactChevron = ({ direction = "left" }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === "left" ? (
      <polyline points="15 18 9 12 15 6" />
    ) : direction === "down" ? (
      <polyline points="6 9 12 15 18 9" />
    ) : (
      <polyline points="9 18 15 12 9 6" />
    )}
  </svg>
);

const resolveCompactPageSize = (limit, perPageValue) => {
  if (perPageValue && typeof perPageValue === "object" && perPageValue.value != null) {
    return Number(perPageValue.value);
  }
  if (typeof perPageValue === "number") return perPageValue;
  return Number(limit) || COMPACT_PAGE_SIZES[0];
};

const CompactPagination = ({
  page,
  totalPages,
  handlePagination,
  limit,
  totalList,
  cssClass = "",
  showPerPageDropdown = false,
  handleLimitChange,
  perPageData,
  perPageValue,
  pageRangeDisplayed = 3,
  marginPagesDisplayed = 1,
  dense = false,
}) => {
  const size = resolveCompactPageSize(limit, perPageValue);
  const current = Number(page) || 1;
  const pagesTotal = Number(totalPages) || 1;
  const count = Number(totalList) || 0;
  const start = count === 0 ? 0 : (current - 1) * size + 1;
  const end = Math.min(current * size, count);
  const options = Array.isArray(perPageData) && perPageData.length
    ? perPageData
    : COMPACT_PAGE_SIZES.map((value) => ({
        label: `${value} / page`,
        value,
      }));
  const control = dense
    ? "inline-flex h-[22px] w-[22px] items-center justify-center rounded-md border border-[#E5E7EB] bg-white"
    : "inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-[#E5E7EB] bg-white";

  return (
    <div className={`flex w-full min-w-0 flex-col border-t border-[#E5E7EB] bg-[#F8FAFC] px-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 ${dense ? "gap-1.5 py-2 sm:gap-3" : "gap-2 py-2.5 sm:gap-3"}`}>
      <p className="min-w-0 text-left text-xs font-medium text-[#64748B]">
        {count === 0 ? "Showing 0 of 0" : `Showing ${start}–${end} of ${count}`}
      </p>

      <div className={classNames("flex min-w-0 flex-wrap items-center justify-end", cssClass)}>
        <ReactPaginate
          forcePage={count === 0 ? undefined : current - 1}
          disableInitialCallback={true}
          previousLabel={<CompactChevron direction="left" />}
          nextLabel={<CompactChevron direction="right" />}
          breakLabel="..."
          pageCount={pagesTotal}
          marginPagesDisplayed={marginPagesDisplayed}
          pageRangeDisplayed={pageRangeDisplayed}
          onPageChange={(event) => {
            handlePagination(event.selected + 1);
          }}
          containerClassName="pagination-compact-list m-0 flex max-w-full list-none flex-wrap items-center justify-end gap-1.5 p-0"
          pageClassName="inline-flex"
          pageLinkClassName={`${control} text-xs font-medium text-[#374151] no-underline`}
          activeClassName="pagination-compact-active"
          activeLinkClassName="!border-[#4A6BB6] !bg-[#4A6BB6] !text-white"
          previousClassName="inline-flex"
          nextClassName="inline-flex"
          previousLinkClassName={`${control} text-[#64748B]`}
          nextLinkClassName={`${control} text-[#64748B]`}
          disabledClassName="cursor-not-allowed opacity-40"
          disabledLinkClassName="cursor-not-allowed"
        />
      </div>

      {showPerPageDropdown && (
        <label className="relative ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2.5 text-xs font-medium text-[#374151] sm:ml-0">
          <span>{size} / page</span>
          <CompactChevron direction="down" />
          <select
            value={size}
            onChange={(event) => {
              const value = Number(event.target.value);
              handleLimitChange?.({
                label: `${value} / page`,
                value,
              });
            }}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label="Rows per page"
          >
            {options.map((option) => {
              const value = option?.value ?? option;
              return (
                <option key={value} value={value}>
                  {option?.label || `${value} / page`}
                </option>
              );
            })}
          </select>
        </label>
      )}
    </div>
  );
};

const Pagination = (props) => {
  const {
    page,
    totalPages,
    handlePagination,
    limit,
    activeListLength,
    totalList,
    cssClass = "",
    newDesign = false,
    showPerPageDropdown = false,
    handleLimitChange,
    perPageData,
    perPageValue,
    perPageSelectCssClass = "",
    perPageMenuMinWidth,
    isClearable = true,
    isFullWidth = false,
    variant = "",
  } = props;
  
  const lengthToMap = useMemo(() => activeListLength, [activeListLength]);

  if (variant === "compact") {
    return (
      <CompactPagination
        page={page}
        totalPages={totalPages}
        handlePagination={handlePagination}
        limit={limit}
        totalList={totalList}
        cssClass={cssClass}
        showPerPageDropdown={showPerPageDropdown}
        handleLimitChange={handleLimitChange}
        perPageData={perPageData}
        perPageValue={perPageValue}
        pageRangeDisplayed={props.pageRangeDisplayed}
        marginPagesDisplayed={props.marginPagesDisplayed}
        dense={props.dense}
      />
    );
  }
 
  return newDesign === true ? (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 custom-pagination">
          {/* Left: Showing X to Y of Z */}
          <div className="hidden sm:block">
              <p className="text-sm text-gray-700">
      <span className="text-xs font-normal">
        {`Showing ${
            page === 1 && lengthToMap?.length > 0
                ? "1"
                : (page - 1) * limit + 1
        } to `}
      </span>
                  <span className="text-xs font-normal">
        {lengthToMap && lengthToMap?.length > 0 ? (
            <>
                {totalList === 0 && lengthToMap?.length > 0
                    ? lengthToMap?.length
                    : page === totalPages
                        ? totalList
                        : parseInt(page) * parseInt(limit)}
            </>
        ) : (
            0
        )}
      </span>
                  <span className="text-xs font-normal">{` of ${totalList} results`}</span>
              </p>
          </div>

          {/* Center: Pagination */}
          <div className={classNames("flex justify-center sm:justify-center w-full sm:w-auto", cssClass)}>
         
              <ReactPaginate
                  forcePage={
                      totalList === 0 ? undefined : parseInt((page === 0 ? 1 : page) - 1)
                  }
                  disableInitialCallback={true}
                  disabledClassName="cursor-not-allowed"
                  disabledLinkClassName="cursor-not-allowed"
                  previousLabel=""
                  nextLabel=""
                  breakLabel="..."
                  breakClassName="break-me"
                  pageCount={totalPages === undefined ? 0 : totalPages}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={(e) => {
                      handlePagination(e.selected + 1);
                  }}
                  containerClassName="pagination flex text-xs font-normal"
                  activeClassName="active"
              />
          </div>

          {/* Right: Per Page Dropdown (conditionally rendered) */}
          {showPerPageDropdown && (
              <div className="flex items-center">
                  <label htmlFor="perPage" className="text-xs font-normal text-gray-700 mr-2">
                      Results per page
                  </label>
                  <SelectOption
                      isClearable={isClearable}
                      newDesign={true}
                      cssClass={perPageSelectCssClass || ""}
                      name='per_page'
                      isSearch={false}
                      data={perPageData}
                      selectedOption={perPageValue}
                      menuPlacement="bottom"
                      menuPosition="fixed"
                      useMenuPortal={true}
                      menuMinWidth={perPageMenuMinWidth}
                      handleChange={(e) => {
                          const perPageValue = e || { label:"10", value: 10 };
                          handleLimitChange(perPageValue);
                      }}
                  />
              </div>
          )}
      </div>
  ) : (
      <div className={classNames('flex sm:justify-between justify-end items-center gap-3 flex-wrap', isFullWidth ? 'min-w-full' : '')}>
          <div className='hidden sm:flex'>
              <div>
                  <p className='text-sm text-gray-700'>
          <span className='font-medium'>
            {`Showing ${
                page === 1 && lengthToMap?.length > 0
                    ? "1"
                    : (page - 1) * limit + 1
            } to `}
          </span>
                      <span className='font-medium'>
            {lengthToMap && lengthToMap?.length > 0 ? (
                <>
                    {totalList === 0 && lengthToMap?.length > 0
                        ? lengthToMap?.length
                        : page === totalPages
                            ? totalList
                            : parseInt(page) * parseInt(limit)}
                </>
            ) : (
                0
            )}
          </span>
                      <span className='font-medium'>{` of ${totalList} results`}</span>
                  </p>
              </div>
          </div>
          <ReactPaginate
              forcePage={
                  totalList === 0 ? undefined : parseInt((page === 0 ? 1 : page) - 1)
              }
              disableInitialCallback={true}
              disabledClassName='cursor-not-allowed'
              disabledLinkClassName='cursor-not-allowed'
              previousLabel=''
              nextLabel=''
              breakLabel='...'
              breakClassName='break-me'
              pageCount={totalPages === undefined ? 0 : totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={(e) => {
                  handlePagination(e.selected + 1);
              }}
              containerClassName={classNames("pagination flex", cssClass)}
              activeClassName='active'
          />
          {/* Additive: only renders when callers opt in via showPerPageDropdown */}
          {showPerPageDropdown && (
              <div className="flex items-center">
                  <label htmlFor="perPage" className="text-xs font-normal text-gray-700 mr-2">
                      Results per page
                  </label>
                  <SelectOption
                      isClearable={false}
                      cssClass={perPageSelectCssClass || "min-w-[5rem]"}
                      name='per_page'
                      isSearch={false}
                      data={
                          Array.isArray(perPageData) && perPageData.length
                              ? perPageData
                              : COMPACT_PAGE_SIZES.map((value) => ({
                                    label: String(value),
                                    value,
                                }))
                      }
                      selectedOption={
                          perPageValue || {
                              label: String(limit || COMPACT_PAGE_SIZES[0]),
                              value: Number(limit) || COMPACT_PAGE_SIZES[0],
                          }
                      }
                      menuPlacement="bottom"
                      menuPosition="fixed"
                      useMenuPortal={true}
                      menuMinWidth={perPageMenuMinWidth}
                      handleChange={(e) => {
                          if (!e || !handleLimitChange) return;
                          handleLimitChange(e);
                      }}
                  />
              </div>
          )}
      </div>
  );
}
export default Pagination;
